import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { LoggerService } from './logger.service';

/**
 * Response from read SAS token endpoint
 */
interface ReadSASTokenResponse {
  readUrl: string;
  expiresAt: string;
}

/**
 * Cached SAS token entry
 */
interface CachedSASToken {
  readUrl: string;
  expiresAt: Date;
}

/**
 * Service for managing Azure Blob Storage SAS tokens for image display
 * Handles caching to minimize backend calls
 */
@Injectable({
  providedIn: 'root',
})
export class SasTokenService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  private readonly API_URL = environment.endPoint;

  // In-memory cache of SAS tokens
  private readonly tokenCache = new Map<string, CachedSASToken>();

  /**
   * Get a read URL with SAS token for displaying an image
   * Uses caching to avoid repeated backend calls
   *
   * @param blobUrl - Base blob URL without SAS token
   * @returns Observable with SAS token URL
   */
  getReadUrl(blobUrl: string): Observable<string> {
    // Return default avatar if no blob URL
    if (!blobUrl || !this.isAzureBlobUrl(blobUrl)) {
      return of(blobUrl);
    }

    // Check cache first
    const cached = this.tokenCache.get(blobUrl);
    if (cached && this.isTokenValid(cached.expiresAt)) {
      this.logger.debug(`Using cached SAS token for ${blobUrl}`);
      return of(cached.readUrl);
    }

    // Request new token from backend
    this.logger.debug(`Requesting new SAS token for ${blobUrl}`);
    return this.requestSASToken(blobUrl);
  }

  /**
   * Request a new SAS token from the backend
   */
  private requestSASToken(blobUrl: string): Observable<string> {
    return this.http
      .get<ReadSASTokenResponse>(`${this.API_URL}/user/storage/read-url`, {
        params: { blobUrl },
      })
      .pipe(
        tap((response) => {
          // Cache the token
          this.tokenCache.set(blobUrl, {
            readUrl: response.readUrl,
            expiresAt: new Date(response.expiresAt),
          });
          this.logger.debug(`Cached SAS token expires at ${response.expiresAt}`);
        }),
        map((response) => response.readUrl),
      );
  }

  /**
   * Check if a cached token is still valid
   * Consider token expired if less than 5 minutes remaining
   */
  private isTokenValid(expiresAt: Date): boolean {
    const now = new Date();
    const expiryTime = new Date(expiresAt);
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);

    return expiryTime > fiveMinutesFromNow;
  }

  /**
   * Check if URL is an Azure Blob Storage URL
   */
  private isAzureBlobUrl(url: string): boolean {
    return !!url && url.includes('.blob.core.windows.net');
  }

  /**
   * Clear the token cache (useful for logout or testing)
   */
  clearCache(): void {
    this.tokenCache.clear();
    this.logger.debug('SAS token cache cleared');
  }

  /**
   * Get multiple read URLs in batch
   * Fetches SAS tokens for uncached URLs in parallel
   */
  getReadUrls(blobUrls: string[]): Observable<Map<string, string>> {
    const result = new Map<string, string>();
    const urlsNeedingTokens: string[] = [];

    // Check cache for each URL
    blobUrls.forEach((blobUrl) => {
      if (!blobUrl || !this.isAzureBlobUrl(blobUrl)) {
        result.set(blobUrl, blobUrl);
        return;
      }

      const cached = this.tokenCache.get(blobUrl);
      if (cached && this.isTokenValid(cached.expiresAt)) {
        result.set(blobUrl, cached.readUrl);
      } else {
        urlsNeedingTokens.push(blobUrl);
      }
    });

    // If all URLs are cached, return immediately
    if (urlsNeedingTokens.length === 0) {
      return of(result);
    }

    // Fetch SAS tokens for uncached URLs in parallel
    const tokenRequests = urlsNeedingTokens.map((blobUrl) =>
      this.requestSASToken(blobUrl).pipe(
        map((readUrl) => ({ blobUrl, readUrl, success: true as const })),
        catchError((err) => {
          this.logger.error(`Failed to get SAS token for ${blobUrl}: ${err}`);
          return of({ blobUrl, readUrl: '', success: false as const });
        }),
      ),
    );

    return forkJoin(tokenRequests).pipe(
      map((responses) => {
        responses.forEach(({ blobUrl, readUrl, success }) => {
          if (success && readUrl) {
            result.set(blobUrl, readUrl);
          }
          // If failed, don't add to result - caller should handle missing URLs
        });
        return result;
      }),
    );
  }
}
